export const digitalMediaLiteracyCourse = {
  id: "digital-media-literacy-2024",
  title: "Digital Media Literacy: Critical Skills for the Information Age",
  shortDescription: "Develop essential skills for navigating, evaluating, and creating digital content responsibly in our interconnected world.",
  description: "Comprehensive course covering digital citizenship, information verification, online safety, content creation ethics, and media manipulation awareness in the digital age.",
  category: "Communication", 
  industryCategory: "Communications & Media",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from digital media specialists and information literacy experts",
  thumbnailUrl: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Critically evaluate digital information sources and detect misinformation",
    "Understand digital privacy, security, and safety best practices",
    "Navigate social media platforms responsibly and ethically",
    "Create digital content that meets ethical and professional standards",
    "Recognize and counter digital manipulation and propaganda techniques",
    "Build positive digital citizenship and online community engagement",
    "Apply media literacy skills to emerging technologies and platforms",
    "Develop strategies for managing digital wellness and screen time"
  ],

  prerequisites: [
    "Basic Computer and Internet Skills",
    "Social Media Familiarity",
    "High School Reading Level",
    "Critical Thinking Interest"
  ],

  skills: [
    "Information Verification",
    "Digital Citizenship",
    "Online Safety",
    "Content Creation",
    "Social Media Literacy",
    "Privacy Management",
    "Critical Thinking",
    "Digital Wellness"
  ],

  tags: [
    "Digital Literacy",
    "Media Literacy",
    "Information Verification",
    "Digital Citizenship",
    "Online Safety",
    "Social Media",
    "Digital Wellness",
    "Critical Thinking"
  ],

  marketData: {
    averageSalary: 64000,
    salaryRange: {
      min: 44000,
      max: 92000
    },
    jobDemand: 4.6,
    marketTrend: "growing",
    careerPaths: [
      "Digital Literacy Educator",
      "Social Media Manager",
      "Content Moderator",
      "Digital Marketing Specialist",
      "Information Specialist",
      "Cybersecurity Awareness Trainer"
    ],
    relatedSkills: [
      "Digital Marketing",
      "Content Creation",
      "Data Privacy",
      "Educational Technology",
      "Information Security",
      "Community Management"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "information-evaluation",
      title: "Information Evaluation and Verification",
      description: "Skills for assessing credibility and accuracy of digital information",
      order: 1,
      estimatedHours: 6.0,
      content: [
        {
          id: "source-credibility",
          title: "Evaluating Source Credibility and Authority",
          description: "Techniques for assessing the reliability of digital information sources",
          type: "video",
          duration: 45
        },
        {
          id: "fact-checking-lab",
          title: "Fact-Checking and Verification Workshop",
          description: "Practice using fact-checking tools and techniques to verify information claims",
          type: "communication-interactive",
          duration: 60,
          interactiveData: {
            exerciseType: "media-analysis",
            instructions: "Analyze various digital media claims using fact-checking techniques and verification tools. Practice identifying misinformation, verifying sources, and determining information credibility.",
            scenario: {
              mediaContent: "You encounter several pieces of information on social media: 1) A viral video claiming to show a recent natural disaster, 2) A news article with alarming health statistics, 3) A social media post sharing investment advice, 4) A political claim with impressive-looking infographics. Use fact-checking tools and techniques to verify the accuracy and credibility of each piece of information."
            },
            hints: [
              "Use reverse image search to verify visual content",
              "Check multiple reliable sources to confirm claims",
              "Look for original sources and primary documentation",
              "Consider the motivation and credibility of information sources"
            ]
          }
        },
        {
          id: "misinformation-patterns",
          title: "Recognizing Misinformation and Manipulation Techniques",
          description: "Understanding how false information spreads and how to identify it",
          type: "text",
          duration: 55,
          textContent: `# Recognizing Misinformation and Manipulation Techniques

## Understanding Information Disorders

### Types of False Information

**Misinformation:**
- **Definition**: False or inaccurate information shared without malicious intent
- **Characteristics**: Often spreads due to genuine belief in the information
- **Examples**: Outdated health advice, honest mistakes in reporting, misunderstood statistics
- **Spread Patterns**: Shared by well-meaning individuals who believe the information
- **Impact**: Can cause harm despite good intentions

**Disinformation:**
- **Definition**: False information deliberately created and spread to deceive
- **Characteristics**: Created with intent to mislead or manipulate
- **Examples**: Fabricated news stories, doctored images, fake expert testimony
- **Spread Patterns**: Often amplified by coordinated networks or bot accounts
- **Impact**: Designed to influence opinions, behaviors, or decision-making

**Malinformation:**
- **Definition**: Genuine information shared to cause harm
- **Characteristics**: True information used inappropriately or out of context
- **Examples**: Private information shared without consent, selective leaking of documents
- **Spread Patterns**: Strategic timing and targeting for maximum damage
- **Impact**: Uses truth as a weapon to harm individuals or groups

### Misinformation Creation and Spread

**Production Methods:**
- **Content Farms**: Organizations that mass-produce low-quality, sensational content
- **Deepfakes**: AI-generated videos that appear to show real people saying or doing things they never did
- **Photo Manipulation**: Altered images that misrepresent events or situations
- **Fake Experts**: Individuals without relevant credentials presented as authorities
- **Pseudoscientific Studies**: Research with flawed methodology designed to support predetermined conclusions

**Distribution Networks:**
- **Social Media Algorithms**: Systems that may amplify engaging but false content
- **Echo Chambers**: Groups where false information gets reinforced rather than challenged
- **Bot Networks**: Automated accounts that artificially amplify certain messages
- **Influencer Amplification**: Trusted figures inadvertently spreading false information
- **Cross-Platform Migration**: False information moving across different digital platforms

## Verification Techniques and Tools

### Source Verification Methods

**Website Analysis:**
- **Domain Investigation**: Checking website ownership, registration date, and location
- **About Page Examination**: Looking for clear information about organization and staff
- **Contact Information**: Verifying that organizations provide legitimate contact details
- **Professional Design**: Assessing whether site appearance matches claimed authority
- **Advertising Content**: Noting what types of ads appear and what they suggest about audience

**Author Verification:**
- **Credential Checking**: Verifying claimed education, experience, and expertise
- **Publication History**: Looking at author's previous work and reputation
- **Institutional Affiliation**: Confirming connections to reputable organizations
- **Social Media Presence**: Examining author's online presence for consistency
- **Peer Recognition**: Checking whether experts in the field recognize the author

**Content Analysis:**
- **Citation Quality**: Examining whether claims are supported by credible sources
- **Methodology Review**: Assessing research methods for scientific or statistical claims
- **Bias Indicators**: Looking for language that suggests strong bias or agenda
- **Fact-Check Coverage**: Seeing whether reputable fact-checkers have evaluated the claims
- **Expert Consensus**: Comparing claims to mainstream expert opinion

### Digital Verification Tools

**Reverse Image Search:**
- **Google Images**: Finding where images originally appeared and in what context
- **TinEye**: Specialized reverse image search with historical tracking
- **Yandex**: Russian search engine often effective for finding image sources
- **Application**: Identifying recycled images, fake event photos, or manipulated content
- **Limitations**: May not catch very recent images or sophisticated manipulations

**Fact-Checking Resources:**
- **Snopes**: Comprehensive fact-checking of urban legends, rumors, and claims
- **PolitiFact**: Political fact-checking with detailed analysis and rating systems
- **FactCheck.org**: Nonpartisan fact-checking of political claims
- **International Fact-Checking Network**: Global network of verified fact-checking organizations
- **Platform-Specific Tools**: Built-in fact-checking features on social media platforms

**Technical Analysis Tools:**
- **Wayback Machine**: Viewing historical versions of websites to track changes
- **WHOIS Database**: Finding information about website ownership and registration
- **Jeffrey's Image Metadata Viewer**: Examining technical information embedded in digital photos
- **FotoForensics**: Analyzing images for signs of digital manipulation
- **InVID**: Video verification tool for journalists and researchers

### Information Triangulation

**Multiple Source Verification:**
- **Primary Sources**: Seeking original documents, eyewitness accounts, or direct evidence
- **Independent Confirmation**: Finding multiple unrelated sources that confirm the same facts
- **Expert Opinion**: Consulting recognized authorities in relevant fields
- **Official Statements**: Checking government agencies, organizations, or institutions involved
- **Timeline Verification**: Ensuring dates and sequences of events make sense

**Context Assessment:**
- **Historical Context**: Understanding background events that provide context
- **Geographic Context**: Verifying location-specific claims and details
- **Cultural Context**: Considering cultural factors that might affect interpretation
- **Political Context**: Understanding political motivations that might influence information
- **Economic Context**: Considering financial interests that might bias information

## Psychological Factors in Information Processing

### Cognitive Biases Affecting Information Evaluation

**Confirmation Bias:**
- **Definition**: Tendency to seek information that confirms existing beliefs
- **Digital Manifestation**: Algorithm-driven content that reinforces existing views
- **Impact**: Creates susceptibility to misinformation that aligns with beliefs
- **Countermeasures**: Actively seeking contradictory evidence and diverse perspectives
- **Example**: Only reading news sources that align with political preferences

**Availability Heuristic:**
- **Definition**: Judging probability based on how easily examples come to mind
- **Digital Manifestation**: Viral content creating perception that rare events are common
- **Impact**: Overestimating risks or benefits based on memorable examples
- **Countermeasures**: Seeking statistical data and representative samples
- **Example**: Believing plane crashes are common because they receive extensive media coverage

**Anchoring Bias:**
- **Definition**: Over-relying on first piece of information encountered
- **Digital Manifestation**: Headlines or social media posts setting initial impressions
- **Impact**: Difficulty revising opinions even when presented with better information
- **Countermeasures**: Deliberately seeking multiple perspectives before forming opinions
- **Example**: Forming opinion about person based on first news story encountered

### Social Psychology Factors

**Social Proof:**
- **Definition**: Tendency to follow what others appear to be doing
- **Digital Manifestation**: Likes, shares, and comments creating perception of popularity
- **Impact**: False information can appear credible if widely shared
- **Countermeasures**: Evaluating information quality independently of popularity
- **Example**: Believing health advice because it has many shares on social media

**Authority Bias:**
- **Definition**: Tendency to trust information from perceived authorities
- **Digital Manifestation**: Fake experts or misleading credentials online
- **Impact**: Accepting false information from people who appear authoritative
- **Countermeasures**: Verifying credentials and expertise independently
- **Example**: Trusting medical advice from someone with an impressive-sounding but irrelevant degree

**In-Group Bias:**
- **Definition**: Favoring information from people perceived as similar to oneself
- **Digital Manifestation**: Trusting information from people in same social networks
- **Impact**: Echo chambers where false information gets reinforced
- **Countermeasures**: Actively seeking diverse perspectives and challenging group think
- **Example**: Accepting political claims without verification because they come from preferred party

## Manipulation Techniques in Digital Media

### Visual Manipulation

**Photo Editing:**
- **Basic Editing**: Cropping, color adjustment, or filtering that changes meaning
- **Object Removal/Addition**: Adding or removing elements to change story
- **Composite Images**: Combining elements from multiple photos to create false scenes
- **Context Manipulation**: Using real photos in wrong context or time period
- **Detection**: Looking for inconsistent lighting, shadows, or image quality

**Video Manipulation:**
- **Selective Editing**: Cutting video to change meaning or remove context
- **Deepfakes**: AI-generated videos showing people saying or doing things they never did
- **Cheap Fakes**: Simple video edits that are easier to create but still deceptive
- **Audio Manipulation**: Changing or adding audio to video content
- **Detection**: Looking for visual artifacts, lip-sync issues, or inconsistent quality

### Textual Manipulation

**Headline Manipulation:**
- **Clickbait**: Sensational headlines designed to generate clicks rather than inform
- **Misleading Framing**: Headlines that misrepresent article content
- **Selective Quotation**: Taking quotes out of context to change meaning
- **False Attribution**: Claiming quotes from people who never said them
- **Emotional Manipulation**: Using emotional language to bypass critical thinking

**Statistical Manipulation:**
- **Cherry-Picking Data**: Selecting only statistics that support desired conclusion
- **Misleading Graphs**: Using visual design to exaggerate or minimize differences
- **Correlation vs. Causation**: Implying causal relationships where none exist
- **Sample Size Issues**: Using inadequate samples to make broad generalizations
- **Outdated Data**: Using old statistics to support current claims

### Algorithmic Amplification

**Filter Bubbles:**
- **Definition**: Algorithmic curation that limits exposure to diverse information
- **Creation**: Platforms showing content similar to previous engagement
- **Impact**: Reinforcing existing beliefs and limiting exposure to contradictory information
- **Detection**: Noticing lack of diverse perspectives in your feeds
- **Countermeasures**: Actively seeking diverse sources and different perspectives

**Echo Chambers:**
- **Definition**: Environments where beliefs get reinforced rather than challenged
- **Formation**: Combination of algorithmic filtering and self-selection
- **Impact**: Increased polarization and resistance to contradictory evidence
- **Detection**: Noticing extreme agreement in your social network
- **Countermeasures**: Engaging with people who have different viewpoints

## Building Information Resilience

### Critical Thinking Practices

**Question Development:**
- **Source Questions**: Who created this? What are their qualifications and motivations?
- **Evidence Questions**: What evidence supports this claim? Is it sufficient and relevant?
- **Context Questions**: What's missing from this story? What's the broader context?
- **Alternative Questions**: What other explanations are possible? What do other sources say?
- **Consequence Questions**: What are the implications if this is true or false?

**Systematic Evaluation:**
- **First Impression**: Notice your initial emotional reaction and set it aside temporarily
- **Source Check**: Verify the credibility and authority of information source
- **Evidence Review**: Examine the quality and sufficiency of supporting evidence
- **Alternative Seeking**: Look for other perspectives and contradictory information
- **Conclusion Formation**: Draw conclusions based on evidence rather than emotion

### Information Hygiene Practices

**Proactive Habits:**
- **Diverse Sources**: Regularly consuming information from various perspectives
- **Fact-Check Integration**: Making fact-checking a routine part of information consumption
- **Pause Before Sharing**: Taking time to verify information before passing it along
- **Metadata Awareness**: Paying attention to publication dates, sources, and context
- **Regular Updates**: Staying current with new misinformation trends and debunking tools

**Community Responsibility:**
- **Gentle Correction**: Politely correcting misinformation when encountered
- **Source Sharing**: Providing credible sources when discussing controversial topics
- **Teaching Others**: Sharing digital literacy skills with family and friends
- **Platform Reporting**: Using reporting tools to flag false or harmful content
- **Positive Modeling**: Demonstrating good information practices in online communities

Understanding misinformation and manipulation techniques enables individuals to navigate the digital information landscape more safely and effectively while contributing to a more informed online community.`
        }
      ],
      quiz: {
        id: "information-evaluation-quiz",
        title: "Information Evaluation and Verification Quiz",
        description: "Test your understanding of information verification and misinformation detection",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main difference between misinformation and disinformation?",
            options: [
              "Misinformation is more harmful than disinformation",
              "Misinformation is false information shared without intent to deceive; disinformation is deliberately created to deceive",
              "Misinformation only occurs online; disinformation occurs offline",
              "There is no significant difference between them"
            ],
            correctAnswer: 1,
            explanation: "Misinformation is false information shared without malicious intent, while disinformation is false information deliberately created and shared with intent to deceive or manipulate."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "When using reverse image search to verify a photo, you should:",
            options: [
              "Only check the first result that appears",
              "Look for the earliest appearance and original context of the image",
              "Assume the image is fake if you find other copies",
              "Only trust results from social media platforms"
            ],
            correctAnswer: 1,
            explanation: "Reverse image search helps you find the original source and context of an image. Look for the earliest appearance and verify the original context to determine if the image is being used accurately."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Confirmation bias in digital media consumption means:",
            options: [
              "Always confirming information with multiple sources",
              "Seeking information that confirms existing beliefs while avoiding contradictory information",
              "Confirming your identity before posting online",
              "Getting confirmation before sharing content"
            ],
            correctAnswer: 1,
            explanation: "Confirmation bias is the tendency to seek, favor, and interpret information that confirms pre-existing beliefs while giving disproportionately less consideration to alternative possibilities."
          }
        ]
      }
    },
    {
      id: "digital-citizenship",
      title: "Digital Citizenship and Online Ethics",
      description: "Responsible behavior and ethical decision-making in digital environments",
      order: 2,
      estimatedHours: 5.5,
      content: [
        {
          id: "digital-footprint",
          title: "Managing Your Digital Footprint and Online Reputation",
          description: "Understanding how digital actions create lasting impacts",
          type: "video",
          duration: 40
        },
        {
          id: "digital-ethics-workshop",
          title: "Digital Ethics Decision-Making Workshop",
          description: "Practice making ethical decisions in complex digital scenarios",
          type: "communication-interactive",
          duration: 55,
          interactiveData: {
            exerciseType: "digital-storytelling",
            instructions: "Navigate complex digital ethics scenarios and create guidance for responsible digital citizenship. Address issues like privacy, consent, digital harassment, and responsible sharing.",
            scenario: {
              storyContext: "You're creating a digital citizenship guide for young adults entering the workforce. Address scenarios including: sharing personal information on professional networks, responding to online harassment, managing work-personal life boundaries on social media, dealing with controversial content, and protecting others' privacy when posting photos or information."
            },
            hints: [
              "Consider both immediate and long-term consequences of digital actions",
              "Think about how actions affect not just yourself but others",
              "Address the gray areas where right and wrong aren't clear-cut",
              "Provide practical strategies people can actually implement"
            ]
          }
        },
        {
          id: "privacy-security",
          title: "Digital Privacy and Security Fundamentals",
          description: "Protecting personal information and securing digital communications",
          type: "text",
          duration: 50,
          textContent: `# Digital Privacy and Security Fundamentals

## Understanding Digital Privacy

### Types of Digital Information

**Personal Identifiable Information (PII):**
- **Direct Identifiers**: Name, address, phone number, email, social security number
- **Indirect Identifiers**: Date of birth, ZIP code, employer, school attended
- **Biometric Data**: Fingerprints, facial recognition data, voice patterns
- **Digital Identifiers**: IP addresses, device IDs, usernames, account numbers
- **Behavioral Data**: Online activity patterns, location history, search queries

**Sensitive Personal Information:**
- **Financial Data**: Bank accounts, credit cards, investment information, income details
- **Health Information**: Medical records, prescriptions, fitness data, mental health information
- **Legal Information**: Criminal records, court documents, legal proceedings
- **Political Information**: Voting records, political donations, party affiliations
- **Personal Relationships**: Family details, romantic relationships, social connections

**Digital Footprint Components:**
- **Active Footprint**: Information you deliberately share online
- **Passive Footprint**: Information collected about you without explicit sharing
- **Metadata**: Hidden information in files and communications
- **Search History**: Records of your online searches and browsing
- **Social Media Activity**: Posts, likes, comments, connections, and engagement patterns

### Privacy Threats and Risks

**Data Collection Practices:**
- **Website Tracking**: Cookies, pixels, and other tracking technologies
- **App Permissions**: Mobile apps requesting access to contacts, location, camera, microphone
- **Social Media Mining**: Platforms analyzing posts, connections, and behavior for insights
- **IoT Device Data**: Smart home devices, wearables, and connected cars collecting information
- **Public Records**: Government databases and public information aggregation

**Commercial Data Use:**
- **Advertising Targeting**: Using personal data to deliver customized advertisements
- **Price Discrimination**: Adjusting prices based on perceived ability to pay
- **Credit and Insurance**: Using data to assess financial risk and set rates
- **Employment Screening**: Employers checking social media and online presence
- **Behavioral Profiling**: Creating detailed profiles for marketing and manipulation

**Security Vulnerabilities:**
- **Data Breaches**: Companies losing customer data to hackers
- **Identity Theft**: Criminals using personal information for fraudulent purposes
- **Account Hijacking**: Unauthorized access to social media, email, or financial accounts
- **Social Engineering**: Manipulating people to reveal sensitive information
- **Malware and Phishing**: Malicious software and fake websites stealing information

## Privacy Protection Strategies

### Information Sharing Guidelines

**Social Media Privacy:**
- **Profile Settings**: Adjusting who can see your posts, photos, and personal information
- **Connection Management**: Being selective about friend and follower requests
- **Post Content**: Avoiding sharing sensitive personal or family information
- **Location Sharing**: Limiting automatic location tagging and check-ins
- **Tag Management**: Controlling how others can tag you in photos and posts

**Professional Online Presence:**
- **Separate Accounts**: Maintaining different profiles for personal and professional use
- **Content Curation**: Ensuring online content aligns with professional image
- **Privacy Settings**: Limiting public access to personal information
- **Background Checks**: Regularly reviewing what information is publicly available about you
- **Reputation Monitoring**: Setting up alerts for mentions of your name online

**Communication Privacy:**
- **Encrypted Messaging**: Using apps that provide end-to-end encryption
- **Email Security**: Being careful about sensitive information in email
- **Video Call Awareness**: Understanding who can see and hear video communications
- **File Sharing**: Using secure methods for sharing sensitive documents
- **Public Wi-Fi Caution**: Avoiding sensitive activities on unsecured networks

### Technical Privacy Tools

**Browser Privacy:**
- **Private/Incognito Mode**: Browsing without saving history locally
- **Cookie Management**: Blocking or limiting tracking cookies
- **Ad Blockers**: Preventing advertisements and tracking scripts
- **VPN Services**: Masking your IP address and encrypting internet traffic
- **Search Engine Alternatives**: Using privacy-focused search engines like DuckDuckGo

**Password Security:**
- **Strong Passwords**: Using complex, unique passwords for each account
- **Password Managers**: Tools to generate and store secure passwords
- **Two-Factor Authentication**: Adding extra security layers to important accounts
- **Regular Updates**: Changing passwords periodically, especially after breaches
- **Security Questions**: Using non-obvious answers to security questions

**Device Security:**
- **Screen Locks**: Protecting devices with PINs, passwords, or biometric locks
- **App Permissions**: Regularly reviewing and limiting app access to data
- **Software Updates**: Keeping operating systems and apps updated for security patches
- **Remote Wipe**: Enabling ability to erase data if device is lost or stolen
- **Backup Encryption**: Securing backup data with encryption

## Digital Ethics Framework

### Ethical Decision-Making Principles

**Respect for Persons:**
- **Autonomy**: Respecting others' right to make their own decisions
- **Consent**: Obtaining permission before sharing others' information or images
- **Dignity**: Treating all people with respect in digital interactions
- **Privacy**: Protecting others' personal information and boundaries
- **Non-exploitation**: Avoiding taking advantage of others' vulnerabilities online

**Beneficence and Non-maleficence:**
- **Do Good**: Using digital platforms to help and support others
- **Avoid Harm**: Preventing digital actions that could hurt individuals or communities
- **Consider Consequences**: Thinking about potential impacts before posting or sharing
- **Protect Vulnerable**: Being especially careful with children, elderly, or marginalized groups
- **Community Benefit**: Contributing positively to online communities

**Justice and Fairness:**
- **Equal Treatment**: Avoiding discrimination in digital interactions
- **Fair Representation**: Accurately representing others and their viewpoints
- **Resource Sharing**: Being equitable in how you use shared digital resources
- **Opportunity Access**: Supporting digital inclusion and access for all
- **Truth and Accuracy**: Sharing accurate information and correcting mistakes

### Common Ethical Dilemmas

**Privacy vs. Transparency:**
- **Scenario**: Balancing openness with personal privacy protection
- **Considerations**: What information benefits others vs. what should remain private
- **Resolution**: Setting clear boundaries about what you will and won't share
- **Example**: Sharing enough about your life to connect with others while protecting sensitive information

**Individual Rights vs. Community Standards:**
- **Scenario**: When personal expression conflicts with community norms
- **Considerations**: Freedom of speech vs. community safety and comfort
- **Resolution**: Finding ways to express yourself that respect community guidelines
- **Example**: Discussing controversial topics in respectful, constructive ways

**Content Sharing vs. Consent:**
- **Scenario**: Wanting to share content that involves other people
- **Considerations**: Others' right to control their image and information
- **Resolution**: Always obtaining permission before sharing others' content
- **Example**: Getting consent before posting photos that include friends or family

## Online Safety and Digital Wellness

### Personal Safety Online

**Identity Protection:**
- **Information Minimization**: Sharing only what's necessary for each platform or service
- **False Information**: Never providing fake information that could cause legal issues
- **Gradual Disclosure**: Revealing personal information slowly in online relationships
- **Verification**: Confirming others' identities before sharing sensitive information
- **Red Flags**: Recognizing when someone requests too much personal information

**Relationship Safety:**
- **Online Dating**: Meeting in public places and telling others about plans
- **Gaming Relationships**: Being cautious about sharing personal information with gaming partners
- **Social Media Connections**: Being selective about who you accept as friends or followers
- **Professional Networks**: Maintaining appropriate boundaries in professional online relationships
- **Children's Safety**: Teaching young people about online stranger danger and inappropriate contact

**Financial Safety:**
- **Secure Shopping**: Using only reputable websites with secure payment systems
- **Scam Recognition**: Identifying common online financial scams and frauds
- **Banking Security**: Using secure connections and official apps for financial transactions
- **Investment Caution**: Being skeptical of online investment opportunities and advice
- **Charity Verification**: Confirming legitimacy of charitable organizations before donating

### Digital Wellness Practices

**Screen Time Management:**
- **Awareness**: Tracking how much time you spend on different digital activities
- **Intentional Use**: Setting specific purposes for device use rather than mindless scrolling
- **Time Limits**: Setting boundaries on recreational screen time
- **Device-Free Zones**: Creating spaces or times without digital devices
- **Sleep Hygiene**: Avoiding screens before bedtime to improve sleep quality

**Mental Health Considerations:**
- **Social Comparison**: Recognizing when social media use creates negative self-comparison
- **FOMO Management**: Dealing with fear of missing out created by social media
- **Cyberbullying Response**: Knowing how to respond to and report online harassment
- **Support Networks**: Building positive online communities and relationships
- **Reality Check**: Remembering that online presentations don't reflect complete reality

**Information Overload Management:**
- **Source Curation**: Choosing quality information sources rather than consuming everything
- **Notification Control**: Managing alerts and notifications to reduce distraction
- **News Consumption**: Balancing staying informed with avoiding news overload
- **Focus Techniques**: Using digital tools to maintain concentration and productivity
- **Regular Breaks**: Taking time away from information consumption for mental processing

## Legal and Regulatory Considerations

### Understanding Digital Rights

**Privacy Laws:**
- **GDPR**: European regulations giving individuals control over personal data
- **CCPA**: California Consumer Privacy Act providing state-level privacy rights
- **COPPA**: Protecting children's privacy online in the United States
- **Sector-Specific Laws**: Healthcare (HIPAA), education (FERPA), and financial (GLBA) privacy rules
- **International Variations**: Understanding different privacy expectations across countries

**Intellectual Property:**
- **Copyright**: Respecting others' creative work and understanding fair use
- **Trademark**: Understanding brand protection and appropriate use of logos/names
- **Creative Commons**: Using and sharing content with open licensing
- **Plagiarism**: Properly attributing others' ideas and work
- **Permission**: Getting consent before using others' content commercially

**Platform Responsibilities:**
- **Terms of Service**: Understanding your agreement with digital platforms
- **Community Guidelines**: Following platform-specific rules for behavior
- **Content Moderation**: Understanding how platforms decide what content to allow
- **Data Portability**: Your rights to access and transfer your data
- **Account Ownership**: Understanding what happens to your content if platforms close

Understanding digital privacy and security enables individuals to navigate online spaces safely while maintaining control over their personal information and digital identity.`
        }
      ],
      quiz: {
        id: "digital-citizenship-quiz",
        title: "Digital Citizenship and Ethics Quiz", 
        description: "Test your understanding of digital citizenship and online ethics",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Before posting a photo that includes other people, you should:",
            options: [
              "Post it immediately if you look good",
              "Get consent from everyone visible in the photo",
              "Only ask permission from people you know well",
              "Post it but avoid tagging anyone"
            ],
            correctAnswer: 1,
            explanation: "Digital ethics requires obtaining consent from everyone visible in photos before posting them online, as people have a right to control their digital image and privacy."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Two-factor authentication adds security by:",
            options: [
              "Using two different passwords",
              "Requiring something you know plus something you have or are",
              "Logging in from two different devices",
              "Having two people verify your identity"
            ],
            correctAnswer: 1,
            explanation: "Two-factor authentication combines something you know (like a password) with something you have (like a phone) or something you are (like a fingerprint) for enhanced security."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Your digital footprint includes:",
            options: [
              "Only the content you deliberately post online",
              "Both content you post and data collected about your online behavior",
              "Only your social media profiles",
              "Only information that is publicly visible"
            ],
            correctAnswer: 1,
            explanation: "Your digital footprint includes both active information you deliberately share and passive information collected about your online behavior, search history, and digital activities."
          }
        ]
      }
    },
    {
      id: "social-media-literacy",
      title: "Social Media Literacy and Platform Dynamics",
      description: "Understanding how social media platforms work and using them effectively",
      order: 3,
      estimatedHours: 5.0,
      content: [
        {
          id: "algorithm-awareness",
          title: "Understanding Social Media Algorithms and Filter Bubbles",
          description: "How platforms curate content and shape user experience",
          type: "video",
          duration: 40
        },
        {
          id: "social-media-strategy-lab",
          title: "Social Media Strategy and Content Creation Lab",
          description: "Create responsible, engaging social media content and develop platform strategies",
          type: "communication-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "digital-storytelling",
            instructions: "Develop a responsible social media strategy for a specific purpose (personal branding, cause advocacy, business promotion, community building). Consider algorithm dynamics, audience engagement, and ethical content creation.",
            scenario: {
              storyContext: "You're helping a local nonprofit organization develop their social media strategy to raise awareness about environmental issues and encourage community action. The organization has limited resources but wants to maximize their impact across multiple platforms while maintaining credibility and avoiding misinformation."
            },
            hints: [
              "Consider how different platforms favor different types of content",
              "Think about timing, frequency, and audience engagement strategies",
              "Address how to handle controversial topics responsibly",
              "Plan for measuring success and adapting strategy based on results"
            ]
          }
        },
        {
          id: "online-communities",
          title: "Building and Participating in Online Communities",
          description: "Best practices for positive online community engagement",
          type: "text",
          duration: 35,
          textContent: `# Building and Participating in Online Communities

## Understanding Platform Dynamics

### Algorithm Functionality

**Content Curation Systems:**
- **Engagement Metrics**: Algorithms prioritize content with high likes, comments, and shares
- **Time Spent**: Platforms favor content that keeps users engaged for longer periods
- **Recency**: Newer content often gets priority in feeds and recommendations
- **Relevance**: Personal interests and past behavior influence what content appears
- **Relationship Strength**: Content from close connections may be prioritized over distant ones

**Personalization Factors:**
- **Demographic Data**: Age, location, and stated interests affecting content selection
- **Behavioral History**: Past clicks, searches, and engagement patterns
- **Network Effects**: What friends and connections engage with influences your feed
- **Device and Context**: Time of day, device type, and location affecting content
- **Explicit Preferences**: Settings and choices you make about content preferences

**Algorithmic Bias:**
- **Filter Bubbles**: Algorithms creating narrow information environments
- **Echo Chambers**: Reinforcement of existing beliefs through selective content exposure
- **Amplification Bias**: Certain types of content getting disproportionate visibility
- **Demographic Bias**: Algorithms potentially discriminating based on user characteristics
- **Commercial Bias**: Paid content and advertising priorities affecting organic content visibility

### Platform-Specific Characteristics

**Facebook/Meta Platforms:**
- **Relationship Focus**: Priority on content from friends and family
- **Long-Form Content**: Support for detailed posts, articles, and discussions
- **Group Dynamics**: Specialized communities around interests and topics
- **Event Organization**: Tools for organizing and promoting real-world gatherings
- **Business Integration**: Extensive tools for commercial and organizational use

**Twitter/X:**
- **Real-Time Information**: Focus on immediate news and current events
- **Brevity**: Character limits encouraging concise communication
- **Public Conversation**: Open discussions and trending topics
- **Influencer Culture**: Platform dynamics that can rapidly amplify certain voices
- **News Integration**: Close relationship with journalism and current events

**Instagram:**
- **Visual Storytelling**: Image and video-centric content creation
- **Aesthetic Focus**: Emphasis on visual appeal and design quality
- **Stories Format**: Temporary content that creates intimacy and immediacy
- **Influencer Marketing**: Strong culture of personal branding and product promotion
- **Discovery Features**: Hashtags and explore features for finding new content

**TikTok:**
- **Algorithm Power**: Highly sophisticated content recommendation system
- **Creative Tools**: Extensive editing and effect capabilities built into platform
- **Trend Culture**: Rapid creation and spread of viral content patterns
- **Music Integration**: Audio as central component of content creation
- **Youth Focus**: Platform demographics and culture skewing younger

**LinkedIn:**
- **Professional Context**: Focus on career development and business networking
- **Thought Leadership**: Platform supporting professional expertise demonstration
- **Industry Focus**: Content organized around professional topics and sectors
- **Networking Tools**: Features designed for professional relationship building
- **Content Formality**: Generally more formal tone and professional standards

## Content Creation Ethics

### Responsible Content Development

**Accuracy and Truth:**
- **Fact Verification**: Checking information before sharing with audiences
- **Source Attribution**: Properly crediting original creators and sources
- **Correction Protocols**: Promptly addressing mistakes and misinformation
- **Context Provision**: Including sufficient background for accurate understanding
- **Uncertainty Acknowledgment**: Being honest about limitations and unknowns

**Representation and Inclusion:**
- **Diverse Perspectives**: Including voices from different backgrounds and experiences
- **Stereotype Avoidance**: Preventing harmful generalizations about groups
- **Cultural Sensitivity**: Respecting different cultural practices and beliefs
- **Accessibility**: Creating content that works for people with disabilities
- **Language Inclusion**: Using language that welcomes rather than excludes

**Privacy and Consent:**
- **Permission Seeking**: Getting consent before featuring others in content
- **Child Protection**: Extra care when creating content involving minors
- **Location Privacy**: Being thoughtful about sharing specific location information
- **Personal Boundaries**: Respecting others' wishes about how they're portrayed
- **Data Protection**: Safeguarding any personal information shared in content

### Content Strategy Development

**Audience Understanding:**
- **Demographic Analysis**: Understanding age, location, interests, and needs of followers
- **Engagement Patterns**: Learning when and how your audience prefers to interact
- **Content Preferences**: Identifying what types of content resonate most
- **Platform Behavior**: Understanding how audience uses different platforms differently
- **Feedback Integration**: Using comments and messages to improve content strategy

**Purpose Clarification:**
- **Goal Setting**: Clear objectives for social media presence and content
- **Value Proposition**: What unique value you provide to your audience
- **Brand Consistency**: Maintaining consistent voice, style, and message across content
- **Success Metrics**: Defining how you'll measure achievement of your goals
- **Evolution Planning**: Allowing for growth and change in purpose over time

**Content Planning:**
- **Editorial Calendar**: Planning content themes and timing in advance
- **Content Mix**: Balancing different types of content (educational, entertaining, promotional)
- **Quality Standards**: Maintaining consistent quality while meeting posting frequency goals
- **Trend Integration**: Thoughtfully participating in relevant trends and conversations
- **Evergreen vs. Timely**: Balancing content that stays relevant with timely, current topics

## Community Building and Engagement

### Positive Community Practices

**Constructive Interaction:**
- **Respectful Dialogue**: Engaging in disagreements without personal attacks
- **Active Listening**: Genuinely considering others' perspectives in online discussions
- **Helpful Responses**: Providing useful information and support to community members
- **Inclusive Language**: Using communication that welcomes diverse participants
- **Conflict De-escalation**: Helping reduce tension when discussions become heated

**Community Support:**
- **New Member Welcome**: Helping newcomers understand community norms and culture
- **Knowledge Sharing**: Contributing expertise and experience to help others
- **Resource Sharing**: Providing useful tools, links, and information
- **Encouragement**: Supporting others' efforts and celebrating successes
- **Mentorship**: Offering guidance to less experienced community members

**Leadership Behaviors:**
- **Modeling**: Demonstrating the behavior you want to see from others
- **Facilitation**: Helping conversations stay productive and on-topic
- **Mediation**: Helping resolve conflicts between community members
- **Vision Setting**: Contributing to community goals and direction
- **Accountability**: Taking responsibility for your role in community dynamics

### Managing Negative Interactions

**Dealing with Trolling:**
- **Recognition**: Identifying when someone is deliberately trying to provoke reactions
- **Non-Engagement**: Avoiding feeding trolls with attention and responses
- **Documentation**: Saving evidence of harassment or abusive behavior
- **Reporting**: Using platform tools to report problematic behavior
- **Community Support**: Working with others to maintain positive environment

**Cyberbullying Response:**
- **Personal Safety**: Protecting your own mental health and well-being
- **Support Networks**: Reaching out to friends, family, or professionals for help
- **Evidence Collection**: Documenting harassment for potential legal or platform action
- **Platform Tools**: Using blocking, muting, and reporting features effectively
- **Professional Help**: Seeking counseling or legal advice when necessary

**Misinformation Management:**
- **Gentle Correction**: Politely providing accurate information when encountering false claims
- **Source Sharing**: Offering credible sources to support corrections
- **Avoid Amplification**: Not spreading misinformation even to debunk it
- **Expert Consultation**: Deferring to qualified experts on technical topics
- **Community Education**: Helping others develop better information evaluation skills

## Digital Influence and Responsibility

### Understanding Online Influence

**Influence Factors:**
- **Follower Count**: Number of people who see your content regularly
- **Engagement Rate**: How actively your audience interacts with your content
- **Credibility**: Perception of your expertise and trustworthiness
- **Network Position**: Your connections to other influential people or organizations
- **Content Quality**: The value and impact of what you share

**Influence Responsibility:**
- **Impact Awareness**: Understanding how your words and actions affect others
- **Fact-Checking**: Extra responsibility to verify information before sharing
- **Platform**: Using influence to promote positive change and accurate information
- **Transparency**: Being honest about relationships, sponsorships, and motivations
- **Role Modeling**: Demonstrating positive online behavior for your audience

### Ethical Influence Practices

**Authentic Engagement:**
- **Genuine Interaction**: Really connecting with your audience rather than just broadcasting
- **Honest Communication**: Being truthful about your experiences and opinions
- **Value-Driven Content**: Creating content that aligns with your authentic values
- **Relationship Building**: Focusing on real connections rather than just follower numbers
- **Long-term Thinking**: Building sustainable influence rather than seeking quick viral moments

**Commercial Transparency:**
- **Disclosure Requirements**: Clearly identifying sponsored content and partnerships
- **Product Honesty**: Sharing genuine opinions about products and services
- **Audience Respect**: Considering your audience's interests in commercial decisions
- **Value Balance**: Ensuring promotional content doesn't overwhelm valuable content
- **Ethics Over Profit**: Maintaining integrity even when it might cost money

**Social Responsibility:**
- **Information Quality**: Sharing accurate, helpful information with your audience
- **Positive Impact**: Using your platform to contribute to positive social change
- **Inclusive Practices**: Ensuring your influence includes rather than excludes diverse voices
- **Community Benefit**: Considering how your content affects broader online communities
- **Future Generations**: Thinking about the digital environment you're helping create

Understanding social media literacy and platform dynamics enables individuals to participate more effectively and responsibly in online communities while maintaining authenticity and positive impact.`
        }
      ],
      quiz: {
        id: "social-media-literacy-quiz",
        title: "Social Media Literacy Quiz",
        description: "Test your understanding of social media dynamics and responsible usage",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Social media algorithms typically prioritize content based on:",
            options: [
              "Chronological order only",
              "Engagement metrics like likes, comments, and time spent",
              "Alphabetical order of usernames",
              "Random selection"
            ],
            correctAnswer: 1,
            explanation: "Social media algorithms prioritize content that receives high engagement (likes, comments, shares) and keeps users on the platform longer, rather than showing content in chronological order."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "A filter bubble refers to:",
            options: [
              "A photo editing feature",
              "The intellectual isolation resulting from algorithms showing only similar content",
              "A privacy setting that filters messages",
              "A type of spam filter"
            ],
            correctAnswer: 1,
            explanation: "A filter bubble is the intellectual isolation that occurs when algorithms create personalized information environments that limit exposure to diverse viewpoints and information."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "When sharing sponsored content, influencers should:",
            options: [
              "Hide the fact that it's sponsored to make it more authentic",
              "Only mention sponsorship if directly asked",
              "Clearly disclose the commercial relationship",
              "Only post sponsored content that they personally use"
            ],
            correctAnswer: 2,
            explanation: "Ethical and legal requirements mandate clear disclosure of commercial relationships in sponsored content, ensuring transparency with audiences about potential conflicts of interest."
          }
        ]
      }
    },
    {
      id: "content-creation-ethics",
      title: "Ethical Content Creation and Digital Storytelling",
      description: "Creating responsible, engaging digital content with ethical considerations",
      order: 4,
      estimatedHours: 4.5,
      content: [
        {
          id: "storytelling-techniques",
          title: "Digital Storytelling Techniques and Narrative Structure",
          description: "Crafting compelling narratives for digital platforms",
          type: "video",
          duration: 35
        },
        {
          id: "content-ethics-project",
          title: "Ethical Content Creation Project",
          description: "Create a digital content series that addresses ethical considerations and social responsibility",
          type: "communication-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "digital-storytelling",
            instructions: "Create a multi-platform digital content series that tells an important story while demonstrating ethical content creation practices. Address issues like consent, representation, accuracy, and social impact.",
            scenario: {
              storyContext: "Develop a digital storytelling campaign about a social issue you care about (mental health awareness, environmental protection, educational equity, etc.). Create content for 3 different platforms that tells a compelling story while following ethical guidelines for representation, fact-checking, and audience respect."
            },
            hints: [
              "Consider how the same story might be told differently across platforms",
              "Think about whose voices should be centered in the story",
              "Address how to verify facts and avoid spreading misinformation",
              "Plan for measuring positive impact rather than just engagement"
            ]
          }
        },
        {
          id: "copyright-fair-use",
          title: "Copyright, Fair Use, and Creative Commons",
          description: "Understanding legal and ethical use of others' creative work",
          type: "text",
          duration: 40,
          textContent: `# Copyright, Fair Use, and Creative Commons

## Understanding Copyright Law

### Copyright Basics

**What Copyright Protects:**
- **Original Works**: Creative expressions fixed in tangible form
- **Creative Categories**: Literature, music, art, photography, video, software, architecture
- **Expression vs. Ideas**: Copyright protects specific expression, not underlying ideas
- **Automatic Protection**: Copyright exists from moment of creation, registration not required
- **Duration**: Generally life of author plus 70 years, or 95 years for corporate works

**Rights Granted to Copyright Holders:**
- **Reproduction**: Right to make copies of the work
- **Distribution**: Right to sell, rent, or give away copies
- **Public Display**: Right to show work publicly (art, photos, etc.)
- **Public Performance**: Right to perform work publicly (music, theater, etc.)
- **Derivative Works**: Right to create new works based on the original
- **Digital Transmission**: Right to control how work is shared online

**What Copyright Doesn't Protect:**
- **Facts and Information**: Basic facts and data cannot be copyrighted
- **Ideas and Concepts**: Only specific expression of ideas, not the ideas themselves
- **Titles and Names**: Short phrases and titles generally not protected
- **Government Works**: Most U.S. government documents are in public domain
- **Expired Works**: Works where copyright has expired are in public domain

### Copyright in Digital Contexts

**Digital Rights Challenges:**
- **Easy Copying**: Digital technology makes copying and sharing effortless
- **Global Distribution**: Internet enables instant worldwide distribution
- **Remix Culture**: Digital tools enable easy creation of derivative works
- **Platform Responsibility**: Questions about liability for user-generated content
- **Fair Use Complexity**: Applying traditional fair use concepts to digital contexts

**Common Copyright Violations:**
- **Unauthorized Sharing**: Posting others' photos, videos, or music without permission
- **Commercial Use**: Using copyrighted material in business contexts without license
- **Whole Work Copying**: Reproducing entire works rather than small portions
- **Derivative Works**: Creating new works based on copyrighted material without permission
- **False Attribution**: Claiming others' work as your own

## Fair Use Doctrine

### Fair Use Factors

**Factor 1: Purpose and Character of Use:**
- **Transformative Use**: Adding new meaning or message to original work
- **Commercial vs. Non-commercial**: Commercial use less likely to be fair, but not automatically unfair
- **Educational Use**: Teaching and scholarship often favored, but not automatically fair
- **Criticism and Commentary**: Using works to critique or comment generally protected
- **Parody**: Humorous commentary on original work often protected

**Factor 2: Nature of Copyrighted Work:**
- **Creative vs. Factual**: More protection for highly creative works
- **Published vs. Unpublished**: Published works more available for fair use
- **Core of Creativity**: Using most creative elements less likely to be fair
- **Availability**: Whether work is readily available to public
- **Commercial vs. Artistic**: Commercial works may have different fair use standards

**Factor 3: Amount and Substantiality Used:**
- **Quantitative Amount**: How much of work is used (percentage)
- **Qualitative Importance**: Whether you used the "heart" of the work
- **Whole Work Use**: Using entire work makes fair use less likely
- **Multiple Excerpts**: Using many small parts may be problematic
- **Context Consideration**: Whether amount used is appropriate for purpose

**Factor 4: Effect on Market Value:**
- **Market Substitution**: Whether use replaces need to buy original
- **Commercial Harm**: Whether use reduces original creator's income
- **Market Development**: Effect on potential future markets
- **License Availability**: Whether you could have licensed the work
- **Cumulative Effect**: Whether widespread similar use would harm market

### Fair Use Applications

**Educational Fair Use:**
- **Classroom Teaching**: Limited use for face-to-face instruction
- **Course Reserves**: Making copyrighted materials available to students
- **Distance Learning**: Fair use in online educational contexts
- **Research**: Using materials for scholarly research and criticism
- **Student Work**: Students using copyrighted materials in assignments

**Commentary and Criticism:**
- **Media Criticism**: Using clips or images to analyze media content
- **Book Reviews**: Quoting from books to support critical analysis
- **Academic Papers**: Using sources to support scholarly arguments
- **Journalism**: Using copyrighted materials in news reporting
- **Social Commentary**: Using popular culture materials to make social points

**Creative Fair Use:**
- **Remix Culture**: Creating new works by combining existing materials
- **Mashups**: Combining multiple sources to create new artistic expressions
- **Documentary Film**: Using archival materials and popular culture clips
- **Art Projects**: Incorporating copyrighted elements in new artistic contexts
- **Internet Memes**: Transformative use of images and cultural references

## Creative Commons and Open Licensing

### Creative Commons License Types

**Attribution (CC BY):**
- **Requirements**: Credit the creator
- **Permissions**: Use for any purpose, including commercial
- **Derivatives**: Allowed with attribution
- **Most Permissive**: Closest to public domain while maintaining attribution
- **Use Cases**: Academic papers, educational resources, general content sharing

**Attribution-ShareAlike (CC BY-SA):**
- **Requirements**: Credit creator and share derivatives under same license
- **Permissions**: Use for any purpose, including commercial
- **Derivatives**: Allowed but must use same license
- **Viral Quality**: Ensures derivative works remain open
- **Use Cases**: Wikipedia, collaborative projects, community resources

**Attribution-NonCommercial (CC BY-NC):**
- **Requirements**: Credit creator and no commercial use
- **Permissions**: Use for non-commercial purposes only
- **Derivatives**: Allowed for non-commercial use
- **Commercial Restriction**: Creator retains commercial rights
- **Use Cases**: Educational materials, personal projects, non-profit content

**Attribution-NoDerivatives (CC BY-ND):**
- **Requirements**: Credit creator and no derivative works
- **Permissions**: Use for any purpose, including commercial
- **Derivatives**: Not allowed - must use work as-is
- **Integrity Protection**: Ensures work isn't modified
- **Use Cases**: Photography, finished artistic works, official documents

**Attribution-NonCommercial-ShareAlike (CC BY-NC-SA):**
- **Requirements**: Credit creator, no commercial use, same license for derivatives
- **Permissions**: Non-commercial use only
- **Derivatives**: Allowed for non-commercial use with same license
- **Community Focus**: Builds commons of freely shareable resources
- **Use Cases**: Educational collaborations, community art projects

**Attribution-NonCommercial-NoDerivatives (CC BY-NC-ND):**
- **Requirements**: Credit creator, no commercial use, no derivatives
- **Permissions**: Non-commercial use only of unmodified work
- **Derivatives**: Not allowed
- **Most Restrictive**: While still allowing sharing
- **Use Cases**: Personal blog posts, artistic photography, finished creative works

### Using Creative Commons Materials

**Finding CC Content:**
- **CC Search**: Official Creative Commons search engine
- **Platform Filters**: YouTube, Flickr, and other platforms have CC content filters
- **Wikimedia Commons**: Large repository of CC and public domain materials
- **Government Sources**: Many government works available under open licenses
- **Educational Repositories**: Universities and organizations sharing educational materials

**Proper Attribution:**
- **TASL Format**: Title, Author, Source, License
- **Complete Information**: Include all required attribution details
- **Link to License**: Provide link to specific CC license used
- **Clear Attribution**: Make attribution obvious and easy to find
- **Maintain Attribution**: Keep attribution when sharing or remixing

## Ethical Content Creation Practices

### Consent and Representation

**Permission Protocols:**
- **Image Rights**: Getting consent before using photos of people
- **Interview Consent**: Clear agreement for recording and using conversations
- **Minor Protection**: Special protections and parental consent for children
- **Vulnerable Populations**: Extra care with sensitive communities
- **Ongoing Consent**: Checking in with people featured in ongoing projects

**Representation Ethics:**
- **Authentic Voices**: Centering people from communities being discussed
- **Stereotype Avoidance**: Preventing harmful generalizations and assumptions
- **Context Preservation**: Maintaining accurate context for quotes and situations
- **Dignity**: Ensuring all people are portrayed with respect and humanity
- **Power Dynamics**: Being aware of how your platform affects those you feature

### Cultural Sensitivity

**Cultural Appropriation Awareness:**
- **Sacred Elements**: Avoiding use of religious or sacred cultural elements
- **Context Understanding**: Learning about cultural significance before use
- **Permission Seeking**: Getting approval from relevant community members
- **Credit and Benefit**: Ensuring original communities benefit from cultural sharing
- **Education vs. Appropriation**: Distinguishing between appreciation and exploitation

**Global Awareness:**
- **Cultural Context**: Understanding how content may be interpreted differently across cultures
- **Language Sensitivity**: Using inclusive language that doesn't exclude cultural groups
- **Holiday Awareness**: Not assuming universal celebration of specific holidays
- **Geographic Sensitivity**: Being aware of global events that might affect content interpretation
- **Accessibility**: Creating content that works across different technological contexts

### Impact Consideration

**Social Responsibility:**
- **Harm Prevention**: Considering potential negative effects of content
- **Positive Contribution**: Creating content that benefits rather than just entertains
- **Community Building**: Using content to strengthen rather than divide communities
- **Education**: Including educational value alongside entertainment
- **Long-term Thinking**: Considering lasting impact of content decisions

**Environmental Awareness:**
- **Digital Carbon Footprint**: Understanding environmental impact of digital content
- **Sustainable Practices**: Choosing environmentally responsible content creation methods
- **Resource Efficiency**: Creating quality content without waste
- **Platform Choice**: Considering environmental practices of platforms and hosting services
- **Audience Education**: Helping audiences understand digital environmental impact

Understanding copyright, fair use, and creative commons enables content creators to use existing materials ethically while protecting their own creative work and contributing to a healthy digital creative ecosystem.`
        }
      ],
      quiz: {
        id: "content-creation-ethics-quiz",
        title: "Content Creation Ethics Quiz",
        description: "Test your understanding of ethical content creation and copyright",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Fair use evaluation considers all of the following factors EXCEPT:",
            options: [
              "The purpose and character of use",
              "The amount and substantiality of the portion used",
              "The popularity of the original work",
              "The effect on the market value of the original work"
            ],
            correctAnswer: 2,
            explanation: "The four factors for fair use are: purpose and character of use, nature of the copyrighted work, amount used, and effect on market value. Popularity is not one of the legal factors."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "A Creative Commons Attribution-NonCommercial (CC BY-NC) license allows:",
            options: [
              "Any use including commercial purposes",
              "Non-commercial use only with attribution to the creator",
              "No derivative works under any circumstances",
              "Use without attribution requirements"
            ],
            correctAnswer: 1,
            explanation: "CC BY-NC licenses allow use for non-commercial purposes only, with attribution required to the original creator. Commercial use requires separate permission."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "When featuring people in your digital content, ethical practice requires:",
            options: [
              "Only getting consent from famous people",
              "Getting consent from everyone featured, especially for identifiable individuals",
              "No consent needed if photos are taken in public",
              "Consent only needed for commercial use"
            ],
            correctAnswer: 1,
            explanation: "Ethical content creation requires obtaining consent from people featured in your content, especially when they are identifiable, regardless of whether the content is commercial or where photos were taken."
          }
        ]
      }
    },
    {
      id: "emerging-technologies",
      title: "Emerging Technologies and Future Digital Literacy",
      description: "Preparing for new digital technologies and evolving literacy needs",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "ai-media-literacy",
          title: "Artificial Intelligence and Automated Content",
          description: "Understanding AI-generated content and its implications for media literacy",
          type: "video",
          duration: 35
        },
        {
          id: "future-literacy-project",
          title: "Future Digital Literacy Planning Project",
          description: "Develop strategies for staying current with emerging digital technologies and literacy needs",
          type: "communication-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "digital-storytelling",
            instructions: "Create a comprehensive plan for maintaining and developing digital literacy skills as technology evolves. Address emerging technologies, changing threats, and evolving best practices for digital citizenship.",
            scenario: {
              storyContext: "Design a digital literacy education program for your community that can adapt to emerging technologies over the next 5-10 years. Consider challenges like AI-generated content, virtual reality interactions, blockchain verification, and technologies that haven't been invented yet."
            },
            hints: [
              "Focus on transferable principles rather than specific technologies",
              "Consider how fundamental concepts apply to new contexts",
              "Think about vulnerable populations who might need special attention",
              "Plan for continuous learning and adaptation"
            ]
          }
        },
        {
          id: "digital-wellness-future",
          title: "Digital Wellness in an Evolving Technological Landscape",
          description: "Maintaining healthy relationships with technology as it continues to evolve",
          type: "text",
          duration: 35,
          textContent: `# Digital Wellness in an Evolving Technological Landscape

## Emerging Technology Challenges

### Artificial Intelligence and Automation

**AI-Generated Content Recognition:**
- **Text Generation**: Identifying writing produced by AI language models
- **Image Synthesis**: Recognizing AI-generated photos and artwork
- **Video Deepfakes**: Detecting AI-created videos of real people
- **Audio Cloning**: Identifying synthetic speech and voice imitation
- **Hybrid Content**: Understanding when human and AI creation combine

**AI Content Implications:**
- **Information Quality**: Assessing reliability of AI-generated information
- **Attribution Challenges**: Understanding authorship when AI is involved
- **Scale of Production**: Dealing with massive amounts of automated content
- **Bias Amplification**: How AI can perpetuate and amplify human biases
- **Original vs. Synthetic**: Distinguishing between authentic and generated content

**AI Literacy Skills:**
- **Tool Understanding**: Knowing how AI content generation tools work
- **Quality Assessment**: Evaluating accuracy and usefulness of AI-generated content
- **Ethical Use**: Using AI tools responsibly for content creation
- **Human Oversight**: Maintaining human judgment in AI-assisted work
- **Transparency**: Being clear when AI tools assist in content creation

### Virtual and Augmented Reality

**Immersive Media Literacy:**
- **Reality Verification**: Distinguishing between real and virtual experiences
- **Perspective Manipulation**: Understanding how VR can influence perception
- **Presence Effects**: Recognizing psychological impact of immersive environments
- **Avatar Identity**: Managing virtual representations of yourself
- **Social VR**: Navigating relationships in virtual spaces

**Privacy in Immersive Environments:**
- **Biometric Data**: Eye tracking, movement patterns, and physiological responses
- **Spatial Privacy**: Information about physical spaces and environments
- **Behavioral Analytics**: Detailed data about movements and interactions
- **Social Interaction**: Private conversations and relationships in virtual spaces
- **Persistent Tracking**: Continuous monitoring across virtual experiences

### Blockchain and Distributed Technologies

**Verification Technologies:**
- **Content Authentication**: Using blockchain to verify content authenticity
- **Digital Ownership**: Understanding NFTs and digital property rights
- **Decentralized Platforms**: Participating in platforms without central authority
- **Consensus Mechanisms**: Understanding how distributed verification works
- **Smart Contracts**: Automated agreements and their implications

**Digital Asset Literacy:**
- **Cryptocurrency Basics**: Understanding digital currencies and their risks
- **Wallet Security**: Protecting digital assets and private keys
- **Transaction Permanence**: Understanding irreversible nature of blockchain transactions
- **Scam Recognition**: Identifying fraudulent schemes in crypto and NFT spaces
- **Environmental Impact**: Understanding energy consumption of blockchain technologies

## Future-Proofing Digital Literacy

### Transferable Core Principles

**Critical Thinking Fundamentals:**
- **Source Evaluation**: Assessing credibility regardless of medium or technology
- **Evidence Assessment**: Evaluating quality of support for claims
- **Bias Recognition**: Identifying bias in any form of communication
- **Logic Analysis**: Recognizing valid and invalid reasoning patterns
- **Context Consideration**: Understanding how context affects meaning

**Information Principles:**
- **Verification Habits**: Checking information before believing or sharing
- **Multiple Perspectives**: Seeking diverse viewpoints on important issues
- **Primary Sources**: Finding original sources when possible
- **Expert Consultation**: Knowing when to defer to qualified authorities
- **Uncertainty Tolerance**: Being comfortable with incomplete information

**Communication Ethics:**
- **Respect for Others**: Treating all people with dignity in any medium
- **Consent and Permission**: Getting approval before involving others in content
- **Truth and Accuracy**: Commitment to honesty regardless of platform
- **Harm Prevention**: Avoiding actions that could hurt individuals or communities
- **Privacy Protection**: Respecting boundaries in any technological context

### Adaptive Learning Strategies

**Continuous Education:**
- **Technology Trends**: Staying informed about emerging technologies and their implications
- **Skill Updates**: Regularly updating digital skills as platforms and tools evolve
- **Community Learning**: Participating in groups focused on digital literacy education
- **Professional Development**: Seeking formal training in digital literacy topics
- **Cross-Generational Learning**: Learning from both younger and older technology users

**Information Networks:**
- **Trusted Sources**: Developing relationships with reliable information providers
- **Expert Networks**: Connecting with professionals who understand emerging technologies
- **Peer Communities**: Building relationships with others committed to digital literacy
- **Educational Institutions**: Maintaining connections with schools and libraries
- **Professional Organizations**: Joining groups focused on digital citizenship and literacy

## Digital Wellness Strategies

### Technology Relationship Management

**Intentional Use:**
- **Purpose-Driven Engagement**: Using technology with specific goals rather than mindless consumption
- **Value Assessment**: Regularly evaluating whether technology use aligns with personal values
- **Boundary Setting**: Creating clear limits on when and how you engage with technology
- **Alternative Activities**: Maintaining non-digital hobbies and interests
- **Mindful Consumption**: Paying attention to how technology affects your mood and behavior

**Digital Minimalism:**
- **Tool Selection**: Choosing only technologies that provide clear value
- **Feature Limitation**: Using only necessary features of devices and apps
- **Notification Management**: Controlling when and how technology demands your attention
- **App Curation**: Regularly reviewing and removing unnecessary applications
- **Subscription Review**: Periodically assessing digital services and subscriptions

### Mental Health and Technology

**Stress Management:**
- **Information Overload**: Recognizing and managing overwhelming amounts of information
- **Social Comparison**: Dealing with negative feelings from social media comparison
- **FOMO (Fear of Missing Out)**: Managing anxiety about not being constantly connected
- **Cyberbullying Response**: Protecting mental health when experiencing online harassment
- **Technology Addiction**: Recognizing problematic technology use patterns

**Positive Engagement:**
- **Community Building**: Using technology to create and maintain meaningful relationships
- **Learning and Growth**: Leveraging technology for personal and professional development
- **Creative Expression**: Using digital tools for artistic and creative pursuits
- **Social Impact**: Employing technology for positive social change
- **Wellness Support**: Using appropriate apps and tools to support physical and mental health

### Physical Health Considerations

**Ergonomic Awareness:**
- **Posture**: Maintaining healthy body position during device use
- **Eye Health**: Protecting vision through appropriate screen use practices
- **Movement**: Regular breaks and physical activity to counter sedentary screen time
- **Sleep Hygiene**: Managing screen time to protect sleep quality
- **Repetitive Strain**: Preventing injuries from repetitive technology use

**Environmental Health:**
- **Blue Light Management**: Understanding and managing exposure to screen light
- **Electromagnetic Fields**: Staying informed about potential health effects of wireless technologies
- **Air Quality**: Considering impact of electronic devices on indoor air quality
- **Noise Management**: Protecting hearing from device-related noise exposure
- **Chemical Exposure**: Understanding potential health effects of device materials and production

## Building Resilient Digital Communities

### Community Digital Literacy

**Collective Learning:**
- **Skill Sharing**: Teaching others what you know about digital literacy
- **Collaborative Problem-Solving**: Working together to address digital challenges
- **Resource Sharing**: Providing access to tools and information for community members
- **Peer Support**: Helping others navigate difficult digital situations
- **Inclusive Education**: Ensuring digital literacy education reaches all community members

**Vulnerability Protection:**
- **Senior Support**: Helping older adults navigate digital scams and privacy issues
- **Youth Education**: Teaching young people about digital citizenship and safety
- **Accessibility**: Ensuring digital literacy resources work for people with disabilities
- **Economic Access**: Addressing digital divides based on economic resources
- **Cultural Competence**: Providing culturally appropriate digital literacy education

### Democratic Participation

**Informed Citizenship:**
- **Election Security**: Understanding digital aspects of voting and election systems
- **Government Transparency**: Using digital tools to access government information
- **Civic Engagement**: Participating in online civic discussions and advocacy
- **Policy Understanding**: Staying informed about technology policy and regulation
- **Digital Rights**: Understanding and advocating for digital rights and freedoms

**Community Organizing:**
- **Digital Organizing**: Using technology effectively for social and political change
- **Online Mobilization**: Coordinating community action through digital platforms
- **Information Campaigns**: Creating and sharing content for social causes
- **Coalition Building**: Using technology to build alliances across different groups
- **Accountability**: Using digital tools to monitor and respond to institutional power

Understanding emerging technologies and future digital literacy needs enables individuals and communities to adapt successfully to technological change while maintaining digital wellness and democratic participation.`
        }
      ],
      quiz: {
        id: "emerging-technologies-quiz",
        title: "Emerging Technologies and Future Literacy Quiz",
        description: "Test your understanding of emerging technologies and adaptive digital literacy",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "When encountering AI-generated content, the most important skill is:",
            options: [
              "Avoiding all AI-generated content",
              "Accepting it as automatically reliable",
              "Evaluating its accuracy and usefulness like any other content",
              "Only trusting content created by humans"
            ],
            correctAnswer: 2,
            explanation: "AI-generated content should be evaluated using the same critical thinking skills as any other content - checking for accuracy, bias, and usefulness rather than automatically accepting or rejecting it based on its origin."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Future-proofing your digital literacy skills means:",
            options: [
              "Learning every new technology as it emerges",
              "Avoiding all new technologies",
              "Focusing on transferable critical thinking principles",
              "Only using established, older technologies"
            ],
            correctAnswer: 2,
            explanation: "Future-proofing digital literacy involves developing transferable critical thinking principles like source evaluation, bias recognition, and ethical reasoning that apply regardless of specific technologies."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Digital wellness in emerging technology contexts requires:",
            options: [
              "Using all available technologies to stay current",
              "Avoiding all new technologies until proven safe",
              "Intentionally choosing technologies that align with your values and needs",
              "Following whatever technology choices influencers recommend"
            ],
            correctAnswer: 2,
            explanation: "Digital wellness involves making intentional choices about which technologies to use based on how they align with your values, support your goals, and contribute to your overall well-being."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "digital-literacy-portfolio",
      title: "Digital Media Literacy Portfolio Project",
      description: "Comprehensive portfolio demonstrating mastery of digital literacy skills across multiple contexts",
      type: "project",
      dueWeeks: 14,
      maxScore: 100,
      instructions: `# Digital Media Literacy Portfolio Project

## Objective
Create a comprehensive portfolio showcasing your development of digital media literacy skills through practical application, critical analysis, and community engagement.

## Portfolio Components
1. **Information Verification Case Studies**: Examples of fact-checking and source verification you've conducted
2. **Digital Citizenship Action Plan**: Personal strategy for responsible online behavior and community engagement
3. **Content Creation Ethics Documentation**: Examples of ethical content creation with reflection on decision-making process
4. **Social Media Strategy Analysis**: Critical analysis of a social media campaign or platform strategy
5. **Future Learning Plan**: Strategy for maintaining digital literacy skills as technology evolves

## Additional Requirements
- Self-assessment of digital literacy growth and areas for continued development
- Evidence of teaching or helping others with digital literacy skills
- Critical reflection on the role of technology in society and personal life
- Action plan for contributing to positive digital communities
- Documentation of emerging technology awareness and adaptation strategies

## Assessment Criteria
- Depth of critical analysis and reflection
- Application of course concepts to real-world digital situations
- Evidence of ethical decision-making in digital contexts
- Quality of information verification and fact-checking skills
- Demonstration of responsible digital citizenship practices
- Professional presentation and clear communication of ideas`,
      submissionFormat: "pdf",
      resources: [
        "Digital portfolio templates and examples",
        "Fact-checking tools and verification resources",
        "Digital citizenship assessment rubrics"
      ]
    }
  ],

  finalExam: {
    id: "digital-media-literacy-final",
    title: "Digital Media Literacy Final Exam",
    description: "Comprehensive examination of digital literacy skills and concepts",
    timeLimit: 120,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "The most effective approach to combating misinformation is:",
        options: [
          "Ignoring all questionable content",
          "Developing critical thinking skills and verification habits",
          "Only trusting information from social media",
          "Believing everything from traditional media sources"
        ],
        correctAnswer: 1,
        explanation: "The most effective approach to combating misinformation involves developing strong critical thinking skills, verification habits, and information literacy practices that can be applied across all media sources and platforms."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "Digital citizenship encompasses:",
        options: [
          "Only technical computer skills",
          "Just following platform rules",
          "Responsible, ethical behavior in all digital interactions",
          "Only protecting your own privacy"
        ],
        correctAnswer: 2,
        explanation: "Digital citizenship encompasses responsible, ethical behavior in all digital interactions, including respecting others' privacy, creating ethical content, fact-checking information, and contributing positively to online communities."
      }
    ]
  },

  quizzes: [
    {
      id: "comprehensive-digital-literacy-quiz",
      title: "Comprehensive Digital Media Literacy Quiz",
      description: "Advanced quiz covering all aspects of digital media literacy",
      timeLimit: 30,
      passingScore: 85,
      attempts: 2,
      questions: [
        {
          id: "comp-q1",
          type: "multiple-choice",
          question: "When evaluating the credibility of an online source, you should consider:",
          options: [
            "Only the visual design of the website",
            "Author credentials, publication standards, bias, and corroborating sources",
            "Only how recently it was published",
            "Whether it confirms your existing beliefs"
          ],
          correctAnswer: 1,
          explanation: "Credible source evaluation requires examining multiple factors: author credentials and expertise, publication standards and editorial oversight, potential bias and motivations, and whether other reliable sources corroborate the information."
        },
        {
          id: "comp-q2",
          type: "multiple-choice",
          question: "Ethical content creation requires:",
          options: [
            "Using any content you find online without attribution",
            "Only creating completely original content",
            "Getting permission for others' content and providing proper attribution",
            "Never collaborating with other creators"
          ],
          correctAnswer: 2,
          explanation: "Ethical content creation involves getting proper permission when using others' content, providing accurate attribution, respecting copyright and Creative Commons licenses, and considering the impact of your content on others."
        },
        {
          id: "comp-q3",
          type: "multiple-choice",
          question: "Filter bubbles and echo chambers can be countered by:",
          options: [
            "Only following people who agree with you",
            "Avoiding all social media",
            "Actively seeking diverse perspectives and questioning your assumptions",
            "Only reading one news source"
          ],
          correctAnswer: 2,
          explanation: "Filter bubbles and echo chambers can be countered by actively seeking diverse perspectives, challenging your own assumptions, consuming information from various sources, and engaging thoughtfully with different viewpoints."
        }
      ]
    }
  ]
};